import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Palette, Upload, Save, Eye, Image as ImageIcon, Type, BadgeCheck, Users, Trophy, Gem, Smartphone, Crop, RotateCcw, X, Headphones, Plus, Trash2, UserRound } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const HOME_ICON_SIZE = 512;
const HOME_ICON_PREVIEW_SIZE = 256;
const SOCIAL_FIELDS = [
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourpage' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourhandle' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: 'https://wa.me/15551234567' },
  { key: 'telegram', label: 'Telegram', placeholder: 'https://t.me/yourchannel' },
  { key: 'discord', label: 'Discord', placeholder: 'https://discord.gg/yourserver' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourchannel' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourhandle' },
  { key: 'website', label: 'Website', placeholder: 'https://yourgroup.com' },
  { key: 'support_email', label: 'Email', placeholder: 'support@yourgroup.com' },
];
const SUPPORT_FIELDS = SOCIAL_FIELDS.filter((field) => field.key !== 'website');
const STATS_FIELDS = [
  { key: 'followers', label: 'Followers', placeholder: '25K+', icon: Users, hint: 'Short value shown in the first stat card.' },
  { key: 'games', label: 'Games', placeholder: '120+', icon: Trophy, hint: 'Short value shown in the center stat card.' },
  { key: 'rewards_claimed', label: 'Rewards Claimed', placeholder: '10M+', icon: Gem, hint: 'Short value shown in the last stat card.' },
];

const createChannelMap = (fields) => Object.fromEntries(fields.map(({ key }) => [key, '']));
const createVisibilityMap = (fields) => Object.fromEntries(fields.map(({ key }) => [key, true]));
const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));
const createAgentId = () => {
  try { return window.crypto?.randomUUID?.() || `agent_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; }
  catch { return `agent_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; }
};

export default function TenantBrandingPage() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHomeIcon, setUploadingHomeIcon] = useState(false);
  const [uploadingSupportIcon, setUploadingSupportIcon] = useState({});
  const [uploadingAgentAvatar, setUploadingAgentAvatar] = useState({});
  const [homeIconCrop, setHomeIconCrop] = useState(null);
  const [homeIconCropDrag, setHomeIconCropDrag] = useState(null);
  const logoInputRef = useRef(null);
  const homeIconInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '', primary_color: '#D4AF37', secondary_color: '#F5D76E',
    welcome_message: '', hero_title: '', tagline: '', description: '', support_email: '',
    verified: true,
    home_screen: { app_name: '', short_name: '', icon_url: '' },
    stats: { followers: '', games: '', rewards_claimed: '' },
    socials: createChannelMap(SOCIAL_FIELDS),
    support: createChannelMap(SUPPORT_FIELDS),
    support_icons: createChannelMap(SUPPORT_FIELDS),
    chat_agents: [],
    social_visibility: createVisibilityMap(SOCIAL_FIELDS),
    support_visibility: createVisibilityMap(SUPPORT_FIELDS),
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await api.get('/api/admin/my-tenant');
      const t = r.data.tenant;
      setTenant(t);
      setForm({
        name: t.name || '',
        primary_color: t.branding?.primary_color || '#D4AF37',
        secondary_color: t.branding?.secondary_color || '#F5D76E',
        welcome_message: t.branding?.welcome_message || '',
        hero_title: t.branding?.hero_title || '',
        tagline: t.branding?.tagline || '',
        description: t.branding?.description || '',
        support_email: t.settings?.support_email || '',
        verified: t.branding?.verified !== false,
        home_screen: {
          app_name: t.branding?.home_screen?.app_name || '',
          short_name: t.branding?.home_screen?.short_name || '',
          icon_url: t.branding?.home_screen?.icon_url || '',
        },
        stats: {
          followers: t.branding?.stats?.followers || '',
          games: t.branding?.stats?.games || '',
          rewards_claimed: t.branding?.stats?.rewards_claimed || '',
        },
        socials: { ...createChannelMap(SOCIAL_FIELDS), ...(t.branding?.socials || {}) },
        support: {
          ...createChannelMap(SUPPORT_FIELDS),
          ...(t.branding?.support || {}),
          support_email: t.branding?.support?.support_email || t.branding?.support?.email || '',
        },
        support_icons: { ...createChannelMap(SUPPORT_FIELDS), ...(t.branding?.support_icons || {}) },
        chat_agents: Array.isArray(t.branding?.chat_agents) ? t.branding.chat_agents : [],
        social_visibility: { ...createVisibilityMap(SOCIAL_FIELDS), ...(t.branding?.social_visibility || {}) },
        support_visibility: { ...createVisibilityMap(SUPPORT_FIELDS), ...(t.branding?.support_visibility || {}) },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tenant');
    } finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/api/admin/my-tenant/branding', form);
      toast.success('Branding updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post('/api/admin/my-tenant/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploadingLogo(false); }
  };

  const openHomeIconCropper = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        setHomeIconCrop({
          src: reader.result,
          fileName: file.name || 'home-screen-icon.png',
          naturalWidth: image.naturalWidth,
          naturalHeight: image.naturalHeight,
          zoom: 1,
          offsetX: 0,
          offsetY: 0,
        });
      };
      image.onerror = () => toast.error('Could not load that image');
      image.src = reader.result;
    };
    reader.onerror = () => toast.error('Could not read that image');
    reader.readAsDataURL(file);
    if (homeIconInputRef.current) homeIconInputRef.current.value = '';
  };

  const updateHomeIconCrop = (patch) => {
    setHomeIconCrop(current => current ? { ...current, ...patch } : current);
  };

  const resetHomeIconCrop = () => {
    updateHomeIconCrop({ zoom: 1, offsetX: 0, offsetY: 0 });
  };

  const getHomeIconCropMetrics = (targetSize = HOME_ICON_SIZE) => {
    if (!homeIconCrop?.naturalWidth || !homeIconCrop?.naturalHeight) return null;
    const zoom = homeIconCrop.zoom || 1;
    const baseScale = Math.max(targetSize / homeIconCrop.naturalWidth, targetSize / homeIconCrop.naturalHeight);
    const width = homeIconCrop.naturalWidth * baseScale * zoom;
    const height = homeIconCrop.naturalHeight * baseScale * zoom;
    const maxX = Math.max(0, (width - targetSize) / 2);
    const maxY = Math.max(0, (height - targetSize) / 2);
    return {
      width,
      height,
      x: ((targetSize - width) / 2) + ((homeIconCrop.offsetX || 0) / 100) * maxX,
      y: ((targetSize - height) / 2) + ((homeIconCrop.offsetY || 0) / 100) * maxY,
    };
  };

  const getHomeIconCropPreviewStyle = () => {
    const metrics = getHomeIconCropMetrics(HOME_ICON_PREVIEW_SIZE);
    if (!metrics) return {};
    return {
      width: `${metrics.width}px`,
      height: `${metrics.height}px`,
      transform: `translate(${metrics.x}px, ${metrics.y}px)`,
    };
  };

  const renderHomeIconCropBlob = () => new Promise((resolve, reject) => {
    const metrics = getHomeIconCropMetrics(HOME_ICON_SIZE);
    if (!homeIconCrop?.src || !metrics) {
      reject(new Error('No icon crop is ready'));
      return;
    }
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = HOME_ICON_SIZE;
      canvas.height = HOME_ICON_SIZE;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, HOME_ICON_SIZE, HOME_ICON_SIZE);
      ctx.drawImage(image, metrics.x, metrics.y, metrics.width, metrics.height);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Could not prepare cropped icon'));
      }, 'image/png', 0.95);
    };
    image.onerror = () => reject(new Error('Could not prepare cropped icon'));
    image.src = homeIconCrop.src;
  });

  const uploadHomeScreenIcon = async (file) => {
    if (!file) return;
    setUploadingHomeIcon(true);
    const fd = new FormData();
    fd.append('file', file, 'home-screen-icon.png');
    try {
      await api.post('/api/admin/my-tenant/home-screen-icon', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Home screen icon updated');
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      return false;
    } finally { setUploadingHomeIcon(false); }
  };

  const confirmHomeIconCrop = async () => {
    try {
      const blob = await renderHomeIconCropBlob();
      const uploaded = await uploadHomeScreenIcon(blob);
      if (uploaded) {
        setHomeIconCrop(null);
        setHomeIconCropDrag(null);
      }
    } catch (e) {
      toast.error(e.message || 'Could not crop that icon');
    }
  };

  const uploadSupportIcon = async (channel, file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setUploadingSupportIcon(current => ({ ...current, [channel]: true }));
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/api/admin/my-tenant/support-icon/${channel}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const iconUrl = res.data?.icon_url;
      if (iconUrl) {
        setForm(current => ({
          ...current,
          support_icons: {
            ...(current.support_icons || {}),
            [channel]: iconUrl,
          },
        }));
      }
      toast.success('Support icon updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Icon upload failed');
    } finally {
      setUploadingSupportIcon(current => ({ ...current, [channel]: false }));
    }
  };

  const uploadChatAgentAvatar = async (agentId, file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setUploadingAgentAvatar(current => ({ ...current, [agentId]: true }));
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/admin/my-tenant/chat-agent-avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const avatarUrl = res.data?.avatar_url;
      if (avatarUrl) {
        setChatAgentField(agentId, 'avatar_url', avatarUrl);
        toast.success('Agent photo uploaded');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Agent photo upload failed');
    } finally {
      setUploadingAgentAvatar(current => ({ ...current, [agentId]: false }));
    }
  };

  const startHomeIconCropDrag = (event) => {
    if (!homeIconCrop) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setHomeIconCropDrag({
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: homeIconCrop.offsetX || 0,
      offsetY: homeIconCrop.offsetY || 0,
    });
  };

  const moveHomeIconCropDrag = (event) => {
    if (!homeIconCropDrag || homeIconCropDrag.pointerId !== event.pointerId) return;
    const deltaX = ((event.clientX - homeIconCropDrag.startX) / HOME_ICON_PREVIEW_SIZE) * 200;
    const deltaY = ((event.clientY - homeIconCropDrag.startY) / HOME_ICON_PREVIEW_SIZE) * 200;
    updateHomeIconCrop({
      offsetX: clampNumber(homeIconCropDrag.offsetX + deltaX, -100, 100),
      offsetY: clampNumber(homeIconCropDrag.offsetY + deltaY, -100, 100),
    });
  };

  const endHomeIconCropDrag = (event) => {
    if (homeIconCropDrag?.pointerId === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      setHomeIconCropDrag(null);
    }
  };

  const resolveLogoUrl = (url) => url?.startsWith('http') ? url : `${API_BASE}${url}`;
  const setBucketField = (bucket, key, value) => {
    setForm(current => ({
      ...current,
      [bucket]: {
        ...current[bucket],
        [key]: value,
      },
    }));
  };

  const setVisibilityField = (bucket, key, checked) => {
    setForm(current => ({
      ...current,
      [bucket]: {
        ...current[bucket],
        [key]: checked,
      },
    }));
  };

  const setStatField = (key, value) => {
    setForm(current => ({
      ...current,
      stats: {
        ...current.stats,
        [key]: value,
      },
    }));
  };

  const setHomeScreenField = (key, value) => {
    setForm(current => ({
      ...current,
      home_screen: {
        ...(current.home_screen || {}),
        [key]: value,
      },
    }));
  };

  const addChatAgent = () => {
    setForm(current => ({
      ...current,
      chat_agents: [
        ...(current.chat_agents || []),
        { id: createAgentId(), name: '', title: 'Support Agent', avatar_url: '', enabled: true },
      ],
    }));
  };

  const setChatAgentField = (id, key, value) => {
    setForm(current => ({
      ...current,
      chat_agents: (current.chat_agents || []).map(agent => (
        agent.id === id ? { ...agent, [key]: value } : agent
      )),
    }));
  };

  const removeChatAgent = (id) => {
    setForm(current => ({
      ...current,
      chat_agents: (current.chat_agents || []).filter(agent => agent.id !== id),
    }));
  };

  const renderCounter = (value, max) => (
    <p className="mt-1 text-[11px] text-gray-500">{(value || '').length}/{max}</p>
  );

  const renderChannelFields = (fields, bucket, visibilityBucket, testPrefix, helperText, options = {}) => (
    <div className="grid md:grid-cols-2 gap-4">
      {fields.map((field) => (
        <div key={`${bucket}-${field.key}`} className="rounded-2xl border border-white/8 bg-black/20 p-3">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-sm text-gray-200">{field.label}</Label>
            <label className="inline-flex items-center gap-2 text-xs text-gray-400">
              <input
                type="checkbox"
                checked={form[visibilityBucket]?.[field.key] !== false}
                onChange={(e) => setVisibilityField(visibilityBucket, field.key, e.target.checked)}
                className="h-4 w-4 accent-[#D4AF37]"
                data-testid={`${testPrefix}-${field.key}-toggle`}
              />
              Show
            </label>
          </div>
          {options.supportIcons && (
            <div className="mt-2 flex items-center gap-3 rounded-xl border border-white/8 bg-black/20 p-2">
              <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl border border-white/10 bg-white/5">
                {form.support_icons?.[field.key] ? (
                  <img
                    src={resolveLogoUrl(form.support_icons[field.key])}
                    alt={`${field.label} icon`}
                    className="h-full w-full object-cover"
                    data-testid={`${testPrefix}-${field.key}-icon-preview`}
                  />
                ) : (
                  <ImageIcon className="h-5 w-5 text-white/45" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-gray-400">Custom icon</p>
                <p className="truncate text-[10px] text-gray-600">PNG/JPG/WebP, square works best</p>
              </div>
              <label
                className="inline-flex h-9 cursor-pointer items-center justify-center rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 text-xs font-semibold text-[#D4AF37] hover:bg-[#D4AF37]/20"
                data-testid={`${testPrefix}-${field.key}-icon-upload`}
              >
                {uploadingSupportIcon[field.key] ? <span className="spinner mr-1.5 h-3.5 w-3.5" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
                Icon
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  disabled={!!uploadingSupportIcon[field.key]}
                  onChange={(e) => {
                    uploadSupportIcon(field.key, e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          )}
          <Input
            value={form[bucket]?.[field.key] || ''}
            onChange={(e) => setBucketField(bucket, field.key, e.target.value)}
            placeholder={field.placeholder}
            className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl"
            data-testid={`${testPrefix}-${field.key}`}
          />
          <p className="mt-1 text-[11px] text-gray-500">{helperText}</p>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tenant-branding-page">
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Palette className="w-6 h-6 text-[#D4AF37]" /> Branding & Customization
        </h2>
        <p className="text-sm text-gray-400 mt-1">Customize your group's landing, login, and home pages</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left 2/3 — Form */}
        <div className="lg:col-span-2 space-y-5">
          {/* Tenant Name + Logo */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[#D4AF37]" /> Identity</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-gray-300">Group Name</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl" data-testid="input-name" />
                {renderCounter(form.name, 28)}
              </div>
              <div>
                <Label className="text-sm text-gray-300">Logo</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden flex-shrink-0" style={{ background: `linear-gradient(135deg, ${form.primary_color}22, ${form.secondary_color}22)`, border: '1px solid rgba(255,255,255,0.08)' }}>
                    {tenant?.branding?.logo_url ? (
                      <img src={resolveLogoUrl(tenant.branding.logo_url)} alt="Logo" className="w-full h-full object-cover" data-testid="current-logo" />
                    ) : (
                      <span className="text-2xl font-bold" style={{ color: form.primary_color }}>{(form.name || 'T').charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <input type="file" ref={logoInputRef} hidden accept="image/*" onChange={e => uploadLogo(e.target.files[0])} />
                    <Button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                      className="rounded-xl bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30"
                      data-testid="btn-upload-logo">
                      {uploadingLogo ? <div className="spinner w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Logo
                    </Button>
                    <p className="text-[10px] text-gray-500 mt-1">PNG/JPG/WebP, square recommended</p>
                    <p className="text-[10px] text-gray-500">This image becomes the big brand logo on the profile page.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Home Screen Install App */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-2 flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-[#D4AF37]" /> Home Screen App
            </h3>
            <p className="text-xs text-gray-400 mb-4">
              Controls the icon and name shown when players add this tenant website to their phone home screen.
            </p>
            <div className="grid md:grid-cols-[auto,1fr] gap-4 items-start">
              <div>
                <Label className="text-sm text-gray-300">App Icon</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <div className="w-20 h-20 rounded-[22px] flex items-center justify-center overflow-hidden flex-shrink-0" style={{ background: 'rgba(0,0,0,0.42)', border: `1px solid ${form.primary_color}55`, boxShadow: `0 0 24px ${form.primary_color}24` }}>
                    {form.home_screen?.icon_url ? (
                      <img src={resolveLogoUrl(form.home_screen.icon_url)} alt="Home screen icon" className="w-full h-full object-cover" data-testid="current-home-screen-icon" />
                    ) : tenant?.branding?.logo_url ? (
                      <img src={resolveLogoUrl(tenant.branding.logo_url)} alt="Brand logo fallback" className="w-full h-full object-cover opacity-70" />
                    ) : (
                      <Smartphone className="w-7 h-7 text-white/55" />
                    )}
                  </div>
                  <div>
                    <input type="file" ref={homeIconInputRef} hidden accept="image/*" onChange={e => openHomeIconCropper(e.target.files[0])} />
                    <Button onClick={() => homeIconInputRef.current?.click()} disabled={uploadingHomeIcon}
                      className="rounded-xl bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30"
                      data-testid="btn-upload-home-screen-icon">
                      {uploadingHomeIcon ? <div className="spinner w-4 h-4 mr-2" /> : <Crop className="w-4 h-4 mr-2" />}
                      Choose & Crop
                    </Button>
                    <p className="text-[10px] text-gray-500 mt-1">Best size: 512 × 512 px square PNG/WebP.</p>
                    <p className="text-[10px] text-gray-500">Leave blank to use the normal brand logo.</p>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-300">Home Screen Name</Label>
                  <Input
                    value={form.home_screen?.app_name || ''}
                    onChange={e => setHomeScreenField('app_name', e.target.value)}
                    placeholder={form.name || 'Your app name'}
                    className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl"
                    data-testid="input-home-screen-name"
                  />
                  <p className="mt-1 text-[11px] text-gray-500">Shown as the installed app title.</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-300">Short Name</Label>
                  <Input
                    value={form.home_screen?.short_name || ''}
                    onChange={e => setHomeScreenField('short_name', e.target.value)}
                    placeholder={(form.home_screen?.app_name || form.name || 'App').slice(0, 12)}
                    className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl"
                    data-testid="input-home-screen-short-name"
                  />
                  <p className="mt-1 text-[11px] text-gray-500">Used where phones have less label space.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2"><Palette className="w-4 h-4 text-[#D4AF37]" /> Brand Colors</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-300">Primary</Label>
                <div className="flex gap-2 mt-1.5">
                  <input type="color" value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                    className="w-12 h-11 rounded-xl border border-white/10 bg-transparent cursor-pointer" data-testid="input-primary-color" />
                  <Input value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                    className="flex-1 bg-black/30 border-white/10 text-white h-11 rounded-xl font-mono text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-sm text-gray-300">Secondary</Label>
                <div className="flex gap-2 mt-1.5">
                  <input type="color" value={form.secondary_color} onChange={e => setForm(f => ({ ...f, secondary_color: e.target.value }))}
                    className="w-12 h-11 rounded-xl border border-white/10 bg-transparent cursor-pointer" data-testid="input-secondary-color" />
                  <Input value={form.secondary_color} onChange={e => setForm(f => ({ ...f, secondary_color: e.target.value }))}
                    className="flex-1 bg-black/30 border-white/10 text-white h-11 rounded-xl font-mono text-sm" />
                </div>
              </div>
            </div>
          </div>

          {/* Texts */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2"><Type className="w-4 h-4 text-[#D4AF37]" /> Text Content</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-gray-300">Hero Title</Label>
                <Input value={form.hero_title} onChange={e => setForm(f => ({ ...f, hero_title: e.target.value }))}
                  placeholder={`Welcome to ${form.name || 'Your Group'}`}
                  className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl" data-testid="input-hero-title" />
                {renderCounter(form.hero_title, 48)}
              </div>
              <div>
                <Label className="text-sm text-gray-300">Tagline / Subtitle</Label>
                <Input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))}
                  placeholder="Fast deposits, instant withdrawals, 24/7 support"
                  className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl" data-testid="input-tagline" />
                {renderCounter(form.tagline, 72)}
              </div>
              <div>
                <Label className="text-sm text-gray-300">Home Welcome Message</Label>
                <Textarea value={form.welcome_message} onChange={e => setForm(f => ({ ...f, welcome_message: e.target.value }))}
                  placeholder="Shown at the top of player's home tab"
                  className="mt-1.5 bg-black/30 border-white/10 text-white rounded-xl min-h-[80px]" rows={3} data-testid="input-welcome-message" />
                {renderCounter(form.welcome_message, 140)}
              </div>
              <div>
                <Label className="text-sm text-gray-300">Brand Description</Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="One clean paragraph shown under the logo on the brand profile page."
                  className="mt-1.5 bg-black/30 border-white/10 text-white rounded-xl min-h-[100px]" rows={4} data-testid="input-description" />
                {renderCounter(form.description, 220)}
              </div>
              <div>
                <Label className="text-sm text-gray-300">Support Email</Label>
                <Input value={form.support_email} onChange={e => setForm(f => ({ ...f, support_email: e.target.value }))}
                  placeholder="support@yourgroup.com"
                  className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl" data-testid="input-support-email" />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={!!form.verified}
                  onChange={(e) => setForm(f => ({ ...f, verified: e.target.checked }))}
                  className="h-4 w-4 accent-[#D4AF37]"
                  data-testid="input-verified"
                />
                Show Official Brand badge
              </label>
            </div>
          </div>

          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-2 flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#D4AF37]" /> Brand Profile Stats</h3>
            <p className="text-xs text-gray-400 mb-4">
              These short values fill the three stat bars on the profile page like the richer premium design.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {STATS_FIELDS.map(({ key, label, placeholder, icon: Icon, hint }) => (
                <div key={key} className="rounded-2xl border border-white/8 bg-black/20 p-3">
                  <div className="flex items-center gap-2 text-white mb-2">
                    <Icon className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <Input
                    value={form.stats?.[key] || ''}
                    onChange={(e) => setStatField(key, e.target.value)}
                    placeholder={placeholder}
                    className="bg-black/30 border-white/10 text-white h-11 rounded-xl"
                    data-testid={`input-stat-${key}`}
                  />
                  <p className="mt-1 text-[11px] text-gray-500">{hint}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-2">Connect With Us</h3>
            <p className="text-xs text-gray-400 mb-4">
              These links show in the brand profile under Connect With Us. Keep the link saved and use Show to turn each row on or off.
            </p>
            {renderChannelFields(SOCIAL_FIELDS, 'socials', 'social_visibility', 'connect-with-us', 'Paste the full link players should open.')}
          </div>

          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-2">Social Media Support</h3>
            <p className="text-xs text-gray-400 mb-4">
              These links show in Add your preferred social media supports on the player side. Use Show to quickly hide any support option without deleting it.
            </p>
            {renderChannelFields(SUPPORT_FIELDS, 'support', 'support_visibility', 'support-channel', 'Visible inside the support picker and support menus.', { supportIcons: true })}
          </div>

          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <Headphones className="w-4 h-4 text-[#D4AF37]" /> Live Chat Agents
                </h3>
                <p className="mt-1 text-xs text-gray-400">
                  These agents appear when players tap Pick your agents. Messages still arrive in the same Live Chat inbox.
                </p>
              </div>
              <Button
                type="button"
                onClick={addChatAgent}
                className="shrink-0 rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30"
                data-testid="btn-add-chat-agent"
              >
                <Plus className="mr-2 h-4 w-4" /> Add Agent
              </Button>
            </div>

            <div className="space-y-3">
              {(form.chat_agents || []).length === 0 && (
                <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 p-5 text-center">
                  <UserRound className="mx-auto mb-2 h-8 w-8 text-white/35" />
                  <p className="text-sm font-semibold text-white">No agents configured yet</p>
                  <p className="mt-1 text-xs text-gray-500">Add agents like Emma, Alex, or Support Team.</p>
                </div>
              )}

              {(form.chat_agents || []).map((agent, index) => (
                <div key={agent.id || index} className="rounded-2xl border border-white/8 bg-black/20 p-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/10 text-sm font-black text-[#D4AF37]">
                        {agent.avatar_url ? (
                          <img src={resolveLogoUrl(agent.avatar_url)} alt="" className="h-full w-full object-cover" />
                        ) : (
                          (agent.name || 'A').charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{agent.name || `Agent ${index + 1}`}</p>
                        <p className="text-[11px] text-gray-500">{agent.title || 'Support Agent'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 text-xs text-gray-400">
                        <input
                          type="checkbox"
                          checked={agent.enabled !== false}
                          onChange={(e) => setChatAgentField(agent.id, 'enabled', e.target.checked)}
                          className="h-4 w-4 accent-[#D4AF37]"
                          data-testid={`chat-agent-${index}-enabled`}
                        />
                        Show
                      </label>
                      <button
                        type="button"
                        onClick={() => removeChatAgent(agent.id)}
                        className="grid h-8 w-8 place-items-center rounded-lg border border-red-500/20 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                        aria-label="Remove agent"
                        data-testid={`chat-agent-${index}-remove`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <Label className="text-xs text-gray-400">Agent Name</Label>
                      <Input
                        value={agent.name || ''}
                        onChange={(e) => setChatAgentField(agent.id, 'name', e.target.value)}
                        placeholder="Agent name"
                        className="mt-1 bg-black/30 border-white/10 text-white h-10 rounded-xl"
                        data-testid={`chat-agent-${index}-name`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Title</Label>
                      <Input
                        value={agent.title || ''}
                        onChange={(e) => setChatAgentField(agent.id, 'title', e.target.value)}
                        placeholder="Support Agent"
                        className="mt-1 bg-black/30 border-white/10 text-white h-10 rounded-xl"
                        data-testid={`chat-agent-${index}-title`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Avatar Photo</Label>
                      <div className="mt-1 flex gap-2">
                        <Input
                          value={agent.avatar_url || ''}
                          onChange={(e) => setChatAgentField(agent.id, 'avatar_url', e.target.value)}
                          placeholder="/uploads/..."
                          className="bg-black/30 border-white/10 text-white h-10 rounded-xl"
                          data-testid={`chat-agent-${index}-avatar`}
                        />
                        <label
                          className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 text-xs font-bold text-[#D4AF37] hover:bg-[#D4AF37]/20"
                          data-testid={`chat-agent-${index}-avatar-upload`}
                        >
                          {uploadingAgentAvatar[agent.id] ? (
                            <span className="spinner h-4 w-4" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                          Photo
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={!!uploadingAgentAvatar[agent.id]}
                            onChange={(e) => {
                              uploadChatAgentAvatar(agent.id, e.target.files?.[0]);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                      <p className="mt-1 text-[11px] text-gray-500">Upload a square agent photo or paste a URL.</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="w-full h-12 rounded-2xl font-bold text-base"
            style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})`, color: '#000' }}
            data-testid="btn-save-branding">
            {saving ? <div className="spinner w-5 h-5 mr-2" /> : <Save className="w-5 h-5 mr-2" />}
            Save All Changes
          </Button>
        </div>

        {/* Right 1/3 — Live Preview */}
        <div className="space-y-5">
          <div className="rounded-2xl p-5 sticky top-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-4"><Eye className="w-4 h-4" /> Live Preview</h3>
            {/* Preview header */}
            <div className="rounded-xl p-3 flex items-center gap-2 mb-3" style={{ background: 'rgba(0,0,0,0.5)', border: `1px solid ${form.primary_color}33` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden" style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})` }}>
                {tenant?.branding?.logo_url
                  ? <img src={resolveLogoUrl(tenant.branding.logo_url)} className="w-full h-full object-cover" />
                  : <span className="text-black font-bold">{(form.name || 'T').charAt(0).toUpperCase()}</span>}
              </div>
              <span className="font-bold text-sm" style={{ background: `linear-gradient(90deg, ${form.primary_color}, ${form.secondary_color})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{form.name || 'Your Group'}</span>
            </div>
            <div className="rounded-xl p-3 flex items-center gap-3 mb-3" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="w-12 h-12 rounded-[14px] flex items-center justify-center overflow-hidden" style={{ background: 'rgba(0,0,0,0.5)', boxShadow: `0 0 18px ${form.primary_color}24` }}>
                {form.home_screen?.icon_url ? (
                  <img src={resolveLogoUrl(form.home_screen.icon_url)} className="w-full h-full object-cover" alt="" />
                ) : tenant?.branding?.logo_url ? (
                  <img src={resolveLogoUrl(tenant.branding.logo_url)} className="w-full h-full object-cover" alt="" />
                ) : (
                  <Smartphone className="w-5 h-5 text-white/50" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Home screen app</p>
                <p className="truncate text-sm font-extrabold text-white">{form.home_screen?.app_name || form.name || 'Your Group'}</p>
                <p className="truncate text-[11px] text-gray-500">{form.home_screen?.short_name || form.home_screen?.app_name || form.name || 'Short name'}</p>
              </div>
            </div>
            {/* Preview hero */}
            <div className="rounded-xl p-4 text-center mb-3" style={{ background: `linear-gradient(135deg, ${form.primary_color}15, ${form.secondary_color}05)`, border: `1px solid ${form.primary_color}33` }}>
              <h3 className="text-base font-bold mb-1" style={{ color: form.primary_color }}>{form.hero_title || `Welcome to ${form.name || 'Your Group'}`}</h3>
              <p className="text-[11px] text-gray-400">{form.tagline || 'Your tagline appears here'}</p>
            </div>
            <div className="rounded-xl p-4 mb-3" style={{ background: 'rgba(11,11,18,0.78)', border: '1px solid rgba(168,85,247,0.24)' }}>
              <div className="flex flex-col items-center text-center">
                <div className="w-28 h-28 rounded-full flex items-center justify-center overflow-hidden mb-3 border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.14)', boxShadow: '0 16px 40px rgba(0,0,0,0.38)' }}>
                  {tenant?.branding?.logo_url
                    ? <img src={resolveLogoUrl(tenant.branding.logo_url)} className="w-full h-full object-cover" alt="" />
                    : <span className="text-white/75 text-3xl font-bold">{(form.name || 'T').charAt(0).toUpperCase()}</span>}
                </div>
                <p className="text-2xl font-extrabold uppercase tracking-wide text-white">{form.name || 'Your Group'}</p>
                {form.verified && (
                  <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-blue-400/50 bg-blue-500/10 px-3 py-1 text-xs font-bold text-blue-300">
                    <BadgeCheck className="w-3.5 h-3.5" /> Official Brand
                  </span>
                )}
                {form.description && <p className="mt-3 text-xs leading-5 text-gray-300">{form.description}</p>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {STATS_FIELDS.map(({ key, label, icon: Icon }) => (
                <div key={key} className="rounded-xl border border-white/10 bg-black/40 p-3 text-center">
                  <Icon className="mx-auto mb-1 h-4 w-4 text-[#D4AF37]" />
                  <p className="text-sm font-extrabold text-white">{form.stats?.[key] || '—'}</p>
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
                </div>
              ))}
            </div>
            {/* Preview CTA button */}
            <button className="w-full py-2.5 rounded-full text-sm font-bold" style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})`, color: '#000' }}>
              Sign Up Button Preview
            </button>
            <div className="mt-4 pt-4 border-t border-white/5 text-[11px] text-gray-500 space-y-1">
              <p>URL: <span className="text-gray-300 font-mono">/{tenant?.slug}</span></p>
              <p>Login: <span className="text-gray-300 font-mono">/{tenant?.slug}/auth</span></p>
              <a href={`/${tenant?.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline mt-1" data-testid="link-visit-tenant">Open landing page →</a>
            </div>
          </div>
        </div>
      </div>
      {homeIconCrop && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-4 py-6 backdrop-blur-sm" data-testid="home-screen-icon-crop-modal">
          <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-[#D4AF37]/25 bg-[#10131c] shadow-2xl shadow-black/70">
            <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
              <div>
                <h3 className="flex items-center gap-2 text-lg font-bold text-white">
                  <Crop className="h-5 w-5 text-[#D4AF37]" /> Crop Home Screen Icon
                </h3>
                <p className="text-xs text-gray-400">Drag the image, zoom, and save a clean 512 × 512 icon.</p>
              </div>
              <button
                type="button"
                onClick={() => setHomeIconCrop(null)}
                className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white"
                aria-label="Close cropper"
                data-testid="btn-close-home-icon-cropper"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5">
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                <div className="flex flex-col items-center">
                  <div
                    className="relative h-64 w-64 touch-none overflow-hidden rounded-[42px] border border-[#D4AF37]/50 bg-black/70 shadow-[0_0_34px_rgba(212,175,55,0.20)]"
                    onPointerDown={startHomeIconCropDrag}
                    onPointerMove={moveHomeIconCropDrag}
                    onPointerUp={endHomeIconCropDrag}
                    onPointerCancel={endHomeIconCropDrag}
                    data-testid="home-icon-crop-canvas"
                  >
                    <img
                      src={homeIconCrop.src}
                      alt="Crop preview"
                      className="absolute left-0 top-0 max-w-none select-none"
                      draggable="false"
                      style={getHomeIconCropPreviewStyle()}
                    />
                    <div className="pointer-events-none absolute inset-0 rounded-[42px] ring-2 ring-inset ring-white/20" />
                    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,transparent_32%,rgba(255,255,255,0.08)_33%,rgba(255,255,255,0.08)_34%,transparent_35%,transparent_65%,rgba(255,255,255,0.08)_66%,rgba(255,255,255,0.08)_67%,transparent_68%),linear-gradient(0deg,transparent_32%,rgba(255,255,255,0.08)_33%,rgba(255,255,255,0.08)_34%,transparent_35%,transparent_65%,rgba(255,255,255,0.08)_66%,rgba(255,255,255,0.08)_67%,transparent_68%)]" />
                  </div>
                  <p className="mt-2 text-center text-[11px] text-gray-500">Drag inside the square to reposition.</p>
                </div>

                <div className="w-full flex-1 space-y-4">
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-gray-300">Zoom</Label>
                      <span className="text-xs font-mono text-[#D4AF37]">{Math.round((homeIconCrop.zoom || 1) * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.01"
                      value={homeIconCrop.zoom}
                      onChange={(e) => updateHomeIconCrop({ zoom: Number(e.target.value) })}
                      className="mt-2 w-full accent-[#D4AF37]"
                      data-testid="home-icon-crop-zoom"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-300">Move left / right</Label>
                    <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={homeIconCrop.offsetX}
                      onChange={(e) => updateHomeIconCrop({ offsetX: Number(e.target.value) })}
                      className="mt-2 w-full accent-[#D4AF37]"
                      data-testid="home-icon-crop-x"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-300">Move up / down</Label>
                    <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={homeIconCrop.offsetY}
                      onChange={(e) => updateHomeIconCrop({ offsetY: Number(e.target.value) })}
                      className="mt-2 w-full accent-[#D4AF37]"
                      data-testid="home-icon-crop-y"
                    />
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/25 p-3 text-xs leading-5 text-gray-400">
                    Phones crop home-screen icons aggressively. Keep the important logo details near the center.
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-white/10 bg-black/20 px-5 py-4 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={resetHomeIconCrop}
                disabled={uploadingHomeIcon}
                className="rounded-xl border-white/10 bg-white/5 text-gray-200 hover:bg-white/10"
                data-testid="btn-reset-home-icon-crop"
              >
                <RotateCcw className="mr-2 h-4 w-4" /> Reset
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setHomeIconCrop(null)}
                disabled={uploadingHomeIcon}
                className="rounded-xl border-white/10 bg-white/5 text-gray-200 hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmHomeIconCrop}
                disabled={uploadingHomeIcon}
                className="rounded-xl bg-[#D4AF37] font-bold text-black hover:bg-[#e6c84d]"
                data-testid="btn-save-cropped-home-icon"
              >
                {uploadingHomeIcon ? <div className="spinner mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />}
                Save Cropped Icon
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
